import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { FaRegClock, FaArrowLeft } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { acknowledgePage } from "../../Utility/nevtanPagesApi";
import BlogSidebar from "./BlogSidebar";
import "./BlogArticle.css";
import "./blog-gaming.css";
import "./blog-layout.css";

const RELATED_STEP = 6;

function thumbOf(p) {
  // Prefer the article's hero image (unique per post) over the thumbnail, which is often
  // a shared promo banner.
  return p?.heroImageUrl || p?.thumbnailUrl || null;
}

// Filename of an image URL, ignoring query strings (S3 presigned tokens differ per request).
function baseName(url) {
  if (!url) return "";
  return url.split("?")[0].split("#")[0].split("/").pop().toLowerCase();
}

// The hero image is already shown above the article, so strip any copies of it out of the
// body HTML to avoid the duplicate.
function removeHeroDuplicates(html, heroUrl) {
  if (!html) return "";
  const heroBase = baseName(heroUrl);
  if (!heroBase) return html;
  return html.replace(/<img\b[^>]*>/gi, (tag) => {
    const m = tag.match(/src\s*=\s*["']([^"']+)["']/i);
    return baseName(m ? m[1] : "") === heroBase ? "" : tag;
  });
}

function dateOf(p) {
  return p?.author?.publishedDate || p?.publishedDate || p?.createdAt || p?.approvedAt || null;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function initials(name) {
  if (!name) return "S";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

const BlogArticle = ({ page, slug, pages = [] }) => {
  const { t } = useTranslation();
  const [relatedShown, setRelatedShown] = useState(3);

  useEffect(() => {
    if (slug) acknowledgePage(slug).catch(() => {});
  }, [slug]);

  // Related pool — posts sharing a tag with this one first, then the rest by date.
  const relatedPool = useMemo(() => {
    if (!Array.isArray(pages) || !page) return [];
    const currentTags = new Set((page.tags || []).map((t) => t?.name).filter(Boolean));
    const others = pages.filter((p) => p && p.slug !== page.slug);
    const byDate = (a, b) => new Date(dateOf(b) || 0) - new Date(dateOf(a) || 0);
    const shared = others.filter((p) => (p.tags || []).some((t) => currentTags.has(t?.name)));
    const rest = others.filter((p) => !shared.includes(p));
    return [...shared.sort(byDate), ...rest.sort(byDate)];
  }, [pages, page]);

  // Reading time — ~200 words per minute over the plain-text of the article.
  const readMinutes = useMemo(() => {
    const text = (page?.htmlContent || "").replace(/<[^>]*>/g, " ");
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
  }, [page]);

  // Article HTML with the duplicate hero image removed.
  const bodyHtml = useMemo(
    () => removeHeroDuplicates(page?.htmlContent, page?.heroImageUrl),
    [page]
  );

  if (!page) return null;

  const authorName = page.author?.name;
  const publishedDate = page.author?.publishedDate || page.createdAt;
  const tags = Array.isArray(page.tags) ? page.tags : [];
  const related = relatedPool.slice(0, relatedShown);
  const hasMoreRelated = relatedShown < relatedPool.length;

  // Article body styling: the API's blog template when present, else the local
  // "gaming" panel styles (blog-gaming.css). Both use the same .nv-post--gaming scope.
  const themed = Boolean(page.templateCss);
  const articleClass = page.templateClass || "nv-post nv-post--gaming";

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription || ""} />
        {page.url && <link rel="canonical" href={page.url} />}
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription || ""} />
        <meta property="og:type" content="article" />
        {page.url && <meta property="og:url" content={page.url} />}
        {page.heroImageUrl && <meta property="og:image" content={page.heroImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.metaDescription || ""} />
        {/* SEO structured data straight from the API */}
        {page.schemaMarkup && (
          <script type="application/ld+json">{page.schemaMarkup}</script>
        )}
      </Helmet>

      <main className="ss-blog" style={{ marginTop: "70px" }}>
        <div className="ss-blog__inner">
          <Link to="/blog" className="ss-blog__back">
            <FaArrowLeft /> {t('blogArticle.backToBlog')}
          </Link>

          <div className="ss-blog__layout">
            {/* Main column */}
            <div className="ss-blog__main">
              {page.heroImageUrl && (
                <img className="ss-blog__hero" src={page.heroImageUrl} alt={page.title} />
              )}

              <div className="ss-blog__meta">
                <span className="ss-blog__avatar">{initials(authorName)}</span>
                <span className="ss-blog__byline">
                  <span className="who">{authorName || t('blog.defaultAuthor')}</span>
                  {publishedDate && <span className="when">{formatDate(publishedDate)}</span>}
                </span>
                <span className="ss-blog__read">
                  <FaRegClock /> {readMinutes} {t('blog.minRead')}
                </span>
                {tags.length > 0 && (
                  <span className="ss-blog__chips">
                    {tags.map((t, i) => (
                      <Link key={t?.slug || t?.name || i} to="/blog" className="ss-blog__chip">
                        {t?.name}
                      </Link>
                    ))}
                  </span>
                )}
              </div>

              {/* The blog template CSS from the API, if present. Rendered as a real <style>
                  so it is never stripped. The local blog-gaming.css covers this otherwise. */}
              {themed && <style dangerouslySetInnerHTML={{ __html: page.templateCss }} />}
              <article
                className={articleClass}
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />

              {related.length > 0 && (
                <section className="ss-blog__related">
                  <h2 className="ss-blog__related-head">{t('blogArticle.youMayAlsoLike')}</h2>
                  <div className="ss-blog__related-grid">
                    {related.map((p) => {
                      const thumb = thumbOf(p);
                      return (
                        <Link key={p.slug} to={`/blog/${p.slug}`} className="ss-relcard">
                          {thumb && <img src={thumb} alt="" loading="lazy" />}
                          <span className="ss-relcard__body">
                            <span className="ss-relcard__title">{p.title}</span>
                            <span className="ss-relcard__date">{formatDate(dateOf(p))}</span>
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                  {hasMoreRelated ? (
                    <button
                      type="button"
                      className="ss-blog__more"
                      onClick={() => setRelatedShown((n) => n + RELATED_STEP)}
                    >
                      {t('blog.seeMore')}
                    </button>
                  ) : (
                    <Link to="/blog" className="ss-blog__more" style={{ textAlign: "center" }}>
                      {t('blogArticle.viewAllPosts')}
                    </Link>
                  )}
                </section>
              )}
            </div>

            {/* Sidebar */}
            <BlogSidebar pages={pages} current={page} />
          </div>
        </div>
      </main>
    </>
  );
};

export default BlogArticle;
